// Mixins
import { userMixins } from "@/app/mixins/modules/user-mixins";
import { switchPetMixins } from "@/app/mixins/modules/switch-pet-mixins";
import { toastMessageMixins } from "@/app/mixins/utils/toast-message-mixins";
import { dateMixins } from "@/app/mixins/utils/date-mixins";
import { imageCompressorMixins } from "@/app/mixins/utils/image-compressor-mixins";

// Constants
import { STATUS_CODES, SUCCESS_MSG } from "@/app/constants/status-constants";

// Third party libraries
import DatePicker from "vue2-datepicker";

// Services
import PetsService from "@/app/services/main/pets/pets-service";

// Util
import { vuelidateUtil } from "@/utils/vuelidate-util.js";

// Components
const FileUpload = () =>
    import(
        /* webpackPrefetch: true, webpackChunkName: "file-upload" */ "@/app/views/main-view/_shared/_components/file-upload/file-upload.vue"
    );

export default {
    name: "PetProfileContainer",
    components: {
        DatePicker,
        FileUpload,
    },
    mixins: [userMixins, switchPetMixins, toastMessageMixins, dateMixins, imageCompressorMixins],
    data() {
        return {
            imageToCrop: "",
            imageSrc: "",
            petName: "",
            petBirth: "",
            petBreed: "",
            petGender: "",
            fileBlob: null,
            isImageChanged: false,
            isAPILoading: false,
            isDeleteSuccess: false,
        };
    },
    computed: {
        validationRules() {
            let fields = {
                petName: {},
                petBirth: {},
            };

            fields = vuelidateUtil.setFieldsRequired(fields, true);
            fields = vuelidateUtil.setIsFutureDate(fields, "petBirth");
            fields = vuelidateUtil.setPetNameLength(fields, "petName");

            return fields;
        },
        hasImage() {
            return !!this.imageSrc;
        },
        checkPetListLength() {
            let petLength = this.$store.getters.getUserDetails.pets
                ? this.$store.getters.getUserDetails.pets.length
                : 0;

            return petLength > 1;
        },
        isInvalid() {
            const { petName, petBirth } = this.$v;
            const vuelidateValidation =
                !petName.required || !petName.petNameLength || !petBirth.required || !petBirth.isFutureDate;
            let isInvalid = false;

            if (vuelidateValidation || !this.compareFormDataDetails) {
                isInvalid = true;
            }

            return isInvalid;
        },
        compareFormDataDetails() {
            const compareImages = this.imageSrc !== this.getActivePet.imageUrl;
            const comparePetName = this.petName !== this.getActivePet.name;
            let compareBirthDate = true;

            if (this.petBirth) {
                compareBirthDate = this.dateToISO(this.petBirth) !== this.getActivePet.birthdate;
            }

            return compareImages || comparePetName || compareBirthDate;
        },
    },
    created() {
        this.getPetData();
    },
    methods: {
        refreshActivePetData() {
            this.getPetData();
        },
        getPetData() {
            this.isAPILoading = true;
            PetsService.getPetById(this.getActivePet.id).then((res) => {
                this.isAPILoading = false;
                if (res.status === STATUS_CODES.SUCCESS) {
                    let updatedPet = { ...this.getActivePet, ...res.data };
                    let updatedUserPet = this.userDetails.pets.map((item) => {
                        if (item.id === updatedPet.id) {
                            Object.assign(item, updatedPet);
                        }
                        return item;
                    });

                    this.$store.commit("setActivePet", updatedPet);
                    this.$store.commit("setUserDetails", { ...this.userDetails, pets: [...updatedUserPet] });
                    this.populateData();
                } else {
                    const { ERROR_GET_PET_DETAILS } = this.toastMessages.pet();
                    this.showAPIErrorToast(ERROR_GET_PET_DETAILS, res);
                }
            });
        },
        populateData() {
            const { name, breed, birthdate, imageUrl, gender } = this.getActivePet;
            this.petName = name;
            this.petBreed = breed.name;
            this.petBirth = this.formatDatetoDisplay(birthdate);
            this.imageSrc = imageUrl;
            this.petGender = gender === "M" ? "Male" : "Female";
        },
        notAfterToday(date) {
            return date > new Date();
        },
        updatePetDetails() {
            if (this.isInvalid) {
                return;
            }

            this.$swal({
                imageHeight: "30px",
                title: "Are you sure you want to update your pet profile?",
                imageUrl: `${require("@/assets/images/_shared/dog-confirm.png")}`,
                confirmButtonText: "Confirm",
                showCancelButton: true,
                cancelButtonText: "Cancel",
                reverseButtons: true,
            }).then((result) => {
                if (result.value) {
                    this.isAPILoading = true;
                    let petDetails = {
                        formData: {
                            name: this.petName,
                            birthdate: this.dateToISO(this.petBirth),
                            isImageChanged: this.isImageChanged,
                        },
                        profileImage: this.fileBlob ? this.fileBlob[0] : "",
                    };

                    PetsService.putPetById(this.getActivePet.id, petDetails).then(async (res) => {
                        if (res.status === STATUS_CODES.SUCCESS_NO_CONTENT) {
                            this.getPetData();
                            await this.getUserDetailsAPI(true, this.getActivePet.id);
                            this.showGenericSuccessToast();
                            // reset data
                            this.fileBlob = null;
                            this.isImageChanged = false;
                            this.imageToCrop = "";
                            this.imageSrc = "";
                            this.populateData();
                        } else {
                            const { ERROR_UPDATE_PET } = this.toastMessages.pet();
                            this.showAPIErrorToast(ERROR_UPDATE_PET, res);
                        }

                        this.isAPILoading = false;
                    });
                }
            });
        },
        deletePet() {
            if (!this.checkPetListLength) {
                return;
            }

            this.$swal({
                imageHeight: "30px",
                imageUrl: `${require("@/assets/images/_shared/dog-confirm.png")}`,
                imageAlt: "Sad Dog",
                title: `Are you sure you want to delete ${this.petName}?`,
                confirmButtonText: "Confirm",
                showCancelButton: true,
                cancelButtonText: "Cancel",
                reverseButtons: true,
            }).then((result) => {
                if (result.value) {
                    this.isAPILoading = true;
                    PetsService.deletePetById(this.getActivePet.id).then((res) => {
                        if (res.status === STATUS_CODES.SUCCESS_NO_CONTENT) {
                            this.isDeleteSuccess = true;
                            const { SUCCESS_DELETE_PET } = this.toastMessages.pet(this.petName);
                            this.showCustomSuccessToast(SUCCESS_MSG.SUCCESS_TITLE, SUCCESS_DELETE_PET);
                            this.getUserDetailsAPI(false, null, true);
                        } else {
                            const { ERROR_DELETE_PET } = this.toastMessages.pet(this.petName);
                            this.showAPIErrorToast(ERROR_DELETE_PET, res);
                        }

                        this.isAPILoading = false;
                    });
                }
            });
        },
        async onUploadImage(event) {
            let valid = await this.validateImage(event.target.files[0]);

            // compress image if more than 2mb
            const file = await this.compressImage(event.target.files[0]);

            // do nothing when user cancels Upload
            if (!valid) {
                // reset uploader
                this.$refs.uploader.value = "";
                return;
            }

            this.isImageChanged = true;
            this.fileBlob = [file];

            // show preview of pet image
            let reader = new FileReader();

            reader.onload = () => {
                this.imageToCrop = reader.result;
                this.$refs.modalFileUpload.show();
            };

            reader.readAsDataURL(file);
        },
        setImageCrop({ image, blob }) {
            this.isImageChanged = true;
            this.imageSrc = image;
            this.fileBlob = blob;
        },
        clearFileInput(event) {
            event.target.value = "";
        },
    },
    // third party libraries
    validations() {
        return this.validationRules;
    },
    beforeRouteLeave(to, from, next) {
        if (this.compareFormDataDetails && to.path !== "/login" && !this.isDeleteSuccess) {
            this.$swal({
                imageHeight: "30px",
                title: "You have unsaved changes!",
                text: "Are you sure you want to leave this page?",
                imageUrl: `${require("@/assets/images/_shared/dog-confirm.png")}`,
                showCancelButton: true,
                confirmButtonColor: "#4B8BFF",
                cancelButtonColor: "#d33",
                confirmButtonText: "Proceed",
                reverseButtons: true,
            }).then((result) => {
                if (result.value) {
                    next(true);
                } else {
                    next(false);
                }
            });
        } else {
            next(true);
        }
    },
};
